import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { DAILY_SUMMARY_ROWS, SUMMARY_COLUMNS, WORK_TYPES } from './workTypes';
import { isEmployeeInactiveOnDay, processEmployeesForMerging } from './App';
import { isTodayInMonth } from './dateUtils';
import './App.css';
import './AdminPage.css';

const EditableCell = ({ value, onSave, disabled, workTypeClass }) => {
    const [editing, setEditing] = useState(false);
    const selectRef = useRef(null);

    const handleSave = (newValue) => {
        if (newValue !== value) {
            onSave(newValue);
        }
        setEditing(false);
    };

    useEffect(() => {
        if (editing && selectRef.current) {
            selectRef.current.focus();
        }
    }, [editing]);

    if (disabled) {
        return <div className="cell-content disabled"></div>;
    }

    return (
        <div className="cell-container" onClick={() => !editing && setEditing(true)}>
            {editing ? (
                <select
                    ref={selectRef}
                    value={value || '근무'}
                    onChange={(e) => handleSave(e.target.value)}
                    onBlur={() => setEditing(false)}
                >
                    {WORK_TYPES.map((type) => (
                        <option key={type} value={type}>{type}</option>
                    ))}
                </select>
            ) : (
                <div className={`cell-content ${workTypeClass}`}>{value}</div>
            )}
        </div>
    );
};

function AdminPage() {
    const [gridData, setGridData] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [isPublicEditable, setIsPublicEditable] = useState(false);
    const navigate = useNavigate();
    const { logout } = useAuth();

    const fetchGridData = (year, month) => {
        axios.get('/api/schedules/grid', { params: { year, month } })
            .then((response) => {
                setGridData(response.data);
                setIsPublicEditable(response.data.isEditable);
            })
            .catch((error) => console.error('Error fetching grid data:', error));
    };

    useEffect(() => {
        fetchGridData(currentDate.getFullYear(), currentDate.getMonth() + 1);
    }, [currentDate]);

    const handleTogglePublicEdit = async () => {
        const editable = !isPublicEditable;
        try {
            await axios.post('/api/admin/settings/editable', { editable });
            setIsPublicEditable(editable);
            alert(`메인 스케줄이 이제 ${editable ? '수정 가능' : '수정 불가'} 상태입니다.`);
        } catch (error) {
            alert('설정 변경에 실패했습니다.');
        }
    };

    const handleCellSave = (date, employeeName, workType) => {
        axios.put('/api/schedules/cell', { date, employeeName, workType })
            .then(() => fetchGridData(currentDate.getFullYear(), currentDate.getMonth() + 1))
            .catch((error) => alert(`업데이트 실패: ${error.response?.data?.message || '서버 오류'}`));
    };

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const items = Array.from(gridData.employees);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        const orderedIds = items.map((item) => item.id);

        axios.put('/api/admin/employees/order', orderedIds)
            .then(() => setGridData({ ...gridData, employees: items }))
            .catch((error) => {
                alert(`순서 변경 실패: ${error.response?.data?.message || '서버 오류'}`);
                fetchGridData(currentDate.getFullYear(), currentDate.getMonth() + 1);
            });
    };

    const changeMonth = (offset) => {
        setCurrentDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
    };

    const handleDayClick = (day) => {
        if (!day.date) {
            return;
        }

        navigate(`/daily-summary/${currentDate.getFullYear()}/${currentDate.getMonth() + 1}/${day.date}`);
    };

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const processedEmployees = gridData ? processEmployeesForMerging(gridData.employees) : [];
    const monthLabel = `${currentDate.getFullYear()}년 ${currentDate.getMonth() + 1}월`;

    return (
        <div className="App">
            <header className="App-header admin-header">
                <h1>관리자 페이지</h1>
                <div className="month-navigator">
                    <button onClick={() => changeMonth(-1)}>&lt;</button>
                    <span>{monthLabel}</span>
                    <button onClick={() => changeMonth(1)}>&gt;</button>
                </div>
                <div className="admin-controls">
                    <button onClick={() => navigate('/admin/employees')} className="secondary-button">직원관리</button>
                    <button onClick={handleLogout} className="logout-button">로그아웃</button>
                    <label className="edit-mode-toggle">
                        <input type="checkbox" checked={isPublicEditable} onChange={handleTogglePublicEdit} />
                        메인 수정 허용
                    </label>
                </div>
            </header>

            <main className="grid-container">
                {gridData ? (
                    <DragDropContext onDragEnd={onDragEnd}>
                        <table className="schedule-grid admin-grid">
                            <thead>
                                <tr>
                                    <th rowSpan="2" className="info-col">부서</th>
                                    <th rowSpan="2" className="info-col">파트</th>
                                    <th rowSpan="2" className="info-col">직무</th>
                                    <th rowSpan="2" className="info-col">직급</th>
                                    <th rowSpan="2" className="info-col">성명</th>
                                    {gridData.headerDays.map((day, index) => (
                                        <th
                                            key={`admin-date-${index}`}
                                            className={`day-type-${day.dayType} day-col ${isTodayInMonth(currentDate, day.date) ? 'today-col' : ''}`}
                                            onClick={() => handleDayClick(day)}
                                        >
                                            <div className="date-header">{day.date}</div>
                                        </th>
                                    ))}
                                    <th colSpan={SUMMARY_COLUMNS.length} className="summary-title">총계</th>
                                </tr>
                                <tr>
                                    {gridData.headerDays.map((day, index) => (
                                        <th
                                            key={`admin-weekday-${index}`}
                                            className={`day-type-${day.dayType} day-col ${isTodayInMonth(currentDate, day.date) ? 'today-col' : ''}`}
                                            onClick={() => handleDayClick(day)}
                                        >
                                            <div className="weekday-header">{day.dayOfWeek}</div>
                                        </th>
                                    ))}
                                    {SUMMARY_COLUMNS.map((column) => (
                                        <th key={column} className="summary-head-col">{column}</th>
                                    ))}
                                </tr>
                            </thead>

                            <Droppable droppableId="employees">
                                {(provided) => (
                                    <tbody {...provided.droppableProps} ref={provided.innerRef}>
                                        {processedEmployees.map((employee, index) => (
                                            <Draggable key={employee.id} draggableId={String(employee.id)} index={index}>
                                                {(draggableProvided) => (
                                                    <tr
                                                        ref={draggableProvided.innerRef}
                                                        {...draggableProvided.draggableProps}
                                                        {...draggableProvided.dragHandleProps}
                                                    >
                                                        {employee.showDept && <td rowSpan={employee.deptRowSpan} className="info-col">{employee.department}</td>}
                                                        {employee.showPart && <td rowSpan={employee.partRowSpan} className="info-col">{employee.part}</td>}
                                                        {employee.showDuty && <td rowSpan={employee.dutyRowSpan} className="info-col">{employee.duty}</td>}
                                                        <td className="info-col">{employee.position}</td>
                                                        <td className="info-col"><Link to={`/employee/${employee.name}`}>{employee.name}</Link></td>
                                                        {gridData.headerDays.map(({ date, dayType }, i) => {
                                                            const employmentInactive = isEmployeeInactiveOnDay(employee, currentDate, date);
                                                            const workType = employmentInactive ? '' : gridData.data[employee.name]?.[date] || '';
                                                            const fullDate = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(date).padStart(2, '0')}`;

                                                            return (
                                                                <td key={`admin-${date}-${i}`} className={`day-type-${dayType} day-col ${employmentInactive ? 'employment-inactive' : ''} ${isTodayInMonth(currentDate, date) ? 'today-col' : ''}`}>
                                                                    <EditableCell
                                                                        value={workType}
                                                                        onSave={(newWorkType) => handleCellSave(fullDate, employee.name, newWorkType)}
                                                                        disabled={dayType === 'empty' || employmentInactive}
                                                                        workTypeClass={workType ? `work-type-cell-${workType}` : ''}
                                                                    />
                                                                </td>
                                                            );
                                                        })}
                                                        {SUMMARY_COLUMNS.map((column) => (
                                                            <td key={`${employee.id}-${column}`} className="summary-cell summary-body-col">
                                                                {gridData.employeeSummaries[employee.name]?.[column] ?? 0}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </tbody>
                                )}
                            </Droppable>

                            <tfoot>
                                {DAILY_SUMMARY_ROWS.map((key) => (
                                    <tr key={key} className="summary-footer-row">
                                        <td colSpan="5" className="footer-label">{key}</td>
                                        {gridData.headerDays.map((day, index) => (
                                            <td key={`admin-footer-${day.date}-${index}`} className={`day-type-${day.dayType} day-col ${isTodayInMonth(currentDate, day.date) ? 'today-col' : ''}`}>
                                                {day.dayType !== 'empty' ? gridData.dailySummaries[day.date]?.[key] : ''}
                                            </td>
                                        ))}
                                        <td colSpan={SUMMARY_COLUMNS.length} className="summary-blank"></td>
                                    </tr>
                                ))}
                            </tfoot>
                        </table>
                    </DragDropContext>
                ) : (
                    <p>스케줄 데이터가 없습니다.</p>
                )}
            </main>
        </div>
    );
}

export default AdminPage;
